/**
 * Helper функции за навигация между дестинации
 */

/**
 * Скролира до активната дестинация в списъка
 */
export function scrollToActiveDestination(
  activeDestination,
  categoryItemReferences,
  categoryNavReference,
) {
  const container = categoryNavReference.current;
  const activeItem = categoryItemReferences.current?.[activeDestination];

  if (!container || !activeItem) {
    return;
  }

  // iOS Safari: smooth scroll често крашва/цикли при heavy layout changes
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  requestAnimationFrame(() => {
    try {
      activeItem.scrollIntoView({
        behavior: isIOS ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    } catch {
      try {
        activeItem.scrollIntoView();
      } catch {
        // Fallback scroll failed - ignore silently
      }
    }
  });
}

/**
 * Намира default template за дестинация
 */
export function getDefaultTemplate(destinationData) {
  if (!destinationData) {
    return null;
  }

  // Активният темплейт винаги е първият по ред (индекс 0)
  return destinationData.details?.templates?.[0] ?? null;
}

/**
 * Създава обект selectedBook за canvas
 */
export function createSelectedBook(
  currentDestinationData,
  activeDestination,
  destinationsData,
  selectedTemplate,
  activeTemplateIndex,
) {
  if (!currentDestinationData) {
    return null;
  }

  let templateToUse = selectedTemplate;

  if (!templateToUse) {
    templateToUse = getDefaultTemplate(currentDestinationData);

    if (!templateToUse && currentDestinationData.details?.templates?.[activeTemplateIndex]) {
      templateToUse = currentDestinationData.details.templates[activeTemplateIndex];
    }
  }

  // 10.7 / 6.3: един стабилен id за сесията (crypto-safe, предсказуемо за редактор/draft/checkout)
  const radix = 36;
  const randomSliceStart = 2;
  const randomSliceEnd = 11;
  const stableId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? `temp-${crypto.randomUUID()}`
      : `temp-${Date.now()}-${Math.random().toString(radix).slice(randomSliceStart, randomSliceEnd)}`;

  return {
    id: stableId,
    title: currentDestinationData.details?.title,
    collection: currentDestinationData.collection,
    dimensions: currentDestinationData.details?.dimensions,
    pages: currentDestinationData.details?.pages,
    price: currentDestinationData.details?.price,
    selectedTemplate: templateToUse,
    categoryKey: activeDestination,
    category: activeDestination,
    allCategories: destinationsData,
  };
}

/**
 * Setup за swipe gesture navigation
 */
export function setupSwipeNavigation(
  sliderElement,
  categoryNames,
  activeCategory,
  setActiveCategory,
) {
  if (!sliderElement) {
    return () => {};
  }

  let startX, startY, moveX, moveY;
  let isDown = false;

  const handleStart = (e) => {
    isDown = true;
    const pageX = e.pageX || e.touches?.[0]?.pageX;
    const pageY = e.pageY || e.touches?.[0]?.pageY;
    startX = pageX;
    startY = pageY;
  };

  const handleMove = (e) => {
    if (!isDown) {
      return;
    }
    e.preventDefault();
    const pageX = e.pageX || e.touches?.[0]?.pageX;
    const pageY = e.pageY || e.touches?.[0]?.pageY;
    moveX = pageX - startX;
    moveY = pageY - startY;
  };

  const handleEnd = () => {
    if (!isDown) {
      return;
    }
    isDown = false;

    if (Math.abs(moveX) > Math.abs(moveY) && Math.abs(moveX) > 50) {
      const currentIndex = categoryNames.indexOf(activeCategory);
      if (moveX > 0) {
        const prevIndex = currentIndex === 0 ? categoryNames.length - 1 : currentIndex - 1;
        setActiveCategory(categoryNames[prevIndex]);
      } else {
        const nextIndex = (currentIndex + 1) % categoryNames.length;
        setActiveCategory(categoryNames[nextIndex]);
      }
    }
  };

  sliderElement.addEventListener('mousedown', handleStart);
  sliderElement.addEventListener('touchstart', handleStart, { passive: true });
  sliderElement.addEventListener('mousemove', handleMove);
  sliderElement.addEventListener('touchmove', handleMove, { passive: false });
  sliderElement.addEventListener('mouseup', handleEnd);
  sliderElement.addEventListener('touchend', handleEnd, { passive: true });
  sliderElement.addEventListener('mouseleave', handleEnd);

  return () => {
    sliderElement.removeEventListener('mousedown', handleStart);
    sliderElement.removeEventListener('touchstart', handleStart);
    sliderElement.removeEventListener('mousemove', handleMove);
    sliderElement.removeEventListener('touchmove', handleMove);
    sliderElement.removeEventListener('mouseup', handleEnd);
    sliderElement.removeEventListener('touchend', handleEnd);
    sliderElement.removeEventListener('mouseleave', handleEnd);
  };
}
